import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLookup, useRecognizerSlots, type LookupItem } from '../lookups';
import { VariableCreateModal } from './VariableCreateModal';

// Sentinel for "+ New variable…" in the picker (numeric var ids never collide).
const NEW_VARIABLE_OPTION = '__fb_new_variable__';
// Reserved mapping key for the recognizer confidence (0..1) — not a real slot.
const CONFIDENCE_KEY = '__confidence__';

// Per-node editor for Mod_VB_Ask: binds each recognizer slot (and the
// confidence) to a flow variable. The chosen recognizer is read from the
// sibling `recognizer` parameter on the same node, so the slot rows follow
// whatever recognizer the user picked.
//
// Wire format (NodeParameter.Value): a JSON object mapping slotKey -> variableId
//   { "straat": 501, "plaats": 502, "__confidence__": 503 }
// The Lua (Mod_VB_Ask_V1) reads this raw JSON, decodes it and writes each
// recognized value into the mapped variable with mod_SetVariable.

interface Props {
  value: string;
  readOnly: boolean;
  applicationId: number;
  recognizerKey: string | null | undefined;
  commit: (next: string) => void;
}

function parseMapping(raw: string): Record<string, string> {
  if (!raw) return {};
  try {
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (v == null || v === '') continue;
      out[k] = String(v);
    }
    return out;
  } catch {
    return {};
  }
}

function serializeMapping(mapping: Record<string, string>): string {
  const entries = Object.entries(mapping).filter(([, v]) => v);
  if (entries.length === 0) return '{}';
  const obj: Record<string, string> = {};
  for (const [k, v] of entries) obj[k] = v;
  return JSON.stringify(obj);
}

interface RowDef {
  key: string;
  label: string;
}

export function SlotMappingEditor({ value, readOnly, applicationId, recognizerKey, commit }: Props) {
  const initial = useMemo(() => parseMapping(value), [value]);
  const [mapping, setMapping] = useState<Record<string, string>>(initial);
  useEffect(() => setMapping(initial), [initial]);

  const { items: slots, loading: slotsLoading, error: slotsError } = useRecognizerSlots(recognizerKey);
  const { items: variables, loading: varLoading, error: varError } = useLookup('variable', applicationId);

  const [createForKey, setCreateForKey] = useState<string | null>(null);

  const persist = useCallback(
    (next: Record<string, string>) => {
      const serialized = serializeMapping(next);
      if (serialized !== value) commit(serialized);
    },
    [commit, value],
  );

  const setRowVariable = (slotKey: string, varId: string) => {
    if (varId === NEW_VARIABLE_OPTION) {
      setCreateForKey(slotKey);
      return;
    }
    setMapping((prev) => {
      const next = { ...prev };
      if (varId) next[slotKey] = varId;
      else delete next[slotKey];
      persist(next);
      return next;
    });
  };

  // Rows: one per declared slot, then the reserved confidence row.
  const rows = useMemo<RowDef[]>(() => {
    const r: RowDef[] = slots.map((s) => ({ key: String(s.id), label: s.name.trim() || String(s.id) }));
    r.push({ key: CONFIDENCE_KEY, label: 'Confidence (0–1)' });
    return r;
  }, [slots]);

  const grouped = useMemo(() => groupByCategory(variables), [variables]);
  const usedIds = useMemo(() => new Set(Object.values(mapping).filter(Boolean)), [mapping]);

  if (!recognizerKey) {
    return <div className="fb-muted">Pick a Recognizer first to map its slots to variables.</div>;
  }
  if (slotsError) {
    return <div className="fb-error-inline">Failed to load slots: {slotsError}</div>;
  }
  if (varError) {
    return <div className="fb-error-inline">Failed to load variables: {varError}</div>;
  }

  return (
    <div className="fb-slotmap">
      {slotsLoading && rows.length === 1 && <div className="fb-muted">Loading slots…</div>}
      {rows.map((row) => {
        const current = mapping[row.key] ?? '';
        return (
          <div key={row.key} className="fb-slotmap-row">
            <span
              className={`fb-slotmap-label${row.key === CONFIDENCE_KEY ? ' fb-slotmap-label--conf' : ''}`}
              title={row.key}
            >
              {row.label}
            </span>
            <span className="fb-slotmap-arrow">→</span>
            <select
              className="fb-input fb-slotmap-select"
              value={current}
              disabled={readOnly || varLoading}
              onChange={(e) => setRowVariable(row.key, e.target.value)}
            >
              <option value="">{varLoading ? 'Loading…' : '(not stored)'}</option>
              {grouped.map((g, gi) =>
                g.category ? (
                  <optgroup key={`${gi}-${g.category}`} label={g.category}>
                    {g.items.map((it) => (
                      <option
                        key={String(it.id)}
                        value={String(it.id)}
                        disabled={usedIds.has(String(it.id)) && String(it.id) !== current}
                      >
                        {it.name.trim()}
                      </option>
                    ))}
                  </optgroup>
                ) : (
                  g.items.map((it) => (
                    <option
                      key={String(it.id)}
                      value={String(it.id)}
                      disabled={usedIds.has(String(it.id)) && String(it.id) !== current}
                    >
                      {it.name.trim()}
                    </option>
                  ))
                ),
              )}
              {!readOnly && <option value={NEW_VARIABLE_OPTION}>+ New variable…</option>}
            </select>
          </div>
        );
      })}

      <VariableCreateModal
        open={createForKey != null}
        applicationId={applicationId}
        onCancel={() => setCreateForKey(null)}
        onCreated={(id) => {
          const slotKey = createForKey;
          setCreateForKey(null);
          if (!slotKey) return;
          setMapping((prev) => {
            const next = { ...prev, [slotKey]: String(id) };
            persist(next);
            return next;
          });
        }}
      />
    </div>
  );
}

interface Group {
  category: string | null;
  items: LookupItem[];
}

function groupByCategory(items: LookupItem[]): Group[] {
  const result: Group[] = [];
  let currentCat: string | null | undefined = undefined;
  let currentItems: LookupItem[] = [];
  for (const it of items) {
    const cat = (it.category ?? null) as string | null;
    if (cat !== currentCat) {
      currentCat = cat;
      currentItems = [];
      result.push({ category: cat, items: currentItems });
    }
    currentItems.push(it);
  }
  return result;
}
